import {
  ChevronRight,
  Keyboard,
  Package,
  Plus,
  ScanBarcode,
  Search,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  productService,
  type Product,
} from "../../../services/product.service";

interface ProductBatchOption {
  id: string;
  label: string;
  quantity: number;
  mrp: number;
  sellingPrice: number;
  expiryDate?: string;
}

interface AddToCartOptions {
  quantity?: number;
  batchId?: string;
  batchStock?: number;
  rate?: number;
}

interface ProductSearchBarProps {
  searchRef: React.RefObject<HTMLInputElement>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searchResults: Product[];
  compact?: boolean;
  addToCart: (
    product: Product,
    options?: AddToCartOptions,
  ) => void | Promise<void>;
}

function getStockStatus(stock: number | undefined) {
  const qty = stock ?? 0;
  if (qty <= 0)
    return {
      label: "Out of stock",
      color: "text-red-500 bg-red-50 border-red-200",
      dot: "bg-red-500",
    };
  if (qty <= 10)
    return {
      label: `Low: ${qty}`,
      color: "text-amber-600 bg-amber-50 border-amber-200",
      dot: "bg-amber-500",
    };
  return {
    label: `In stock: ${qty}`,
    color: "text-emerald-600 bg-emerald-50 border-emerald-200",
    dot: "bg-emerald-500",
  };
}

function getScheduleBadge(type: string | undefined | null) {
  if (!type || type === "GENERAL") return null;
  const colors: Record<string, string> = {
    SCHEDULE_H: "bg-orange-100 text-orange-700 border-orange-200",
    SCHEDULE_H1: "bg-red-100 text-red-700 border-red-200",
    SCHEDULE_X: "bg-purple-100 text-purple-700 border-purple-200",
    SCHEDULE_G: "bg-blue-100 text-blue-700 border-blue-200",
  };
  const label = type.replace("SCHEDULE_", "Sch. ");
  return {
    label,
    className: colors[type] || "bg-gray-100 text-gray-600 border-gray-200",
  };
}

export function ProductSearchBar({
  searchRef,
  searchTerm,
  setSearchTerm,
  searchResults,
  compact = false,
  addToCart,
}: ProductSearchBarProps) {
  const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const formatExpiry = (value: unknown): string => {
    if (!value) return "";
    const d = new Date(String(value));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", {
      month: "2-digit",
      year: "2-digit",
    });
  };

  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [batchOptions, setBatchOptions] = useState<ProductBatchOption[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [searchResults]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!searchResults.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : searchResults.length - 1,
        );
      } else if (e.key === "Enter" && highlightIndex >= 0) {
        e.preventDefault();
        const picked = searchResults[highlightIndex];
        setSelectedProduct(picked);
        setSearchTerm(picked.name);
        setHighlightIndex(-1);
      }
    },
    [searchResults, highlightIndex, setSearchTerm],
  );

  useEffect(() => {
    let active = true;

    const loadBatches = async () => {
      if (!selectedProduct) {
        setBatchOptions([]);
        setSelectedBatchId("");
        return;
      }

      try {
        const batches = await productService.getBatches({
          productId: selectedProduct.id,
          inStock: true,
        });

        const options: ProductBatchOption[] = (batches || [])
          .filter((b: any) => Number(b?.quantity || 0) > 0)
          .map((b: any) => ({
            id: String(b.id),
            label: String(
              `${b.batchNumber || b.batchNo || b.batchCode || `Batch ${String(b.id).slice(0, 6)}`}` +
                ` | Qty:${toNumber(b.quantity)}` +
                (formatExpiry(b.expiryDate)
                  ? ` | Exp:${formatExpiry(b.expiryDate)}`
                  : "") +
                ` | MRP:${toNumber(b.mrp || selectedProduct.mrp).toFixed(2)}` +
                ` | Sell:${toNumber(b.sellingPrice || selectedProduct.sellingPrice || b.mrp || selectedProduct.mrp).toFixed(2)}`,
            ),
            quantity: toNumber(b.quantity || 0),
            mrp: toNumber(b.mrp || selectedProduct.mrp || 0),
            sellingPrice: toNumber(
              b.sellingPrice ||
                selectedProduct.sellingPrice ||
                b.mrp ||
                selectedProduct.mrp ||
                0,
            ),
            expiryDate: b.expiryDate ? String(b.expiryDate) : undefined,
          }));

        if (!active) return;
        setBatchOptions(options);
        setSelectedBatchId((prev) =>
          prev && options.some((opt) => opt.id === prev)
            ? prev
            : (options[0]?.id ?? ""),
        );
      } catch {
        if (!active) return;
        setBatchOptions([]);
        setSelectedBatchId("");
      }
    };

    void loadBatches();
    return () => {
      active = false;
    };
  }, [selectedProduct]);

  const selectedBatch = batchOptions.find(
    (batch) => batch.id === selectedBatchId,
  );
  const displayMrp = toNumber(selectedBatch?.mrp ?? selectedProduct?.mrp ?? 0);
  const displaySelling = toNumber(
    selectedBatch?.sellingPrice ??
      selectedProduct?.sellingPrice ??
      selectedBatch?.mrp ??
      selectedProduct?.mrp ??
      0,
  );

  const handleAddSelected = async () => {
    if (!selectedProduct) return;

    await addToCart(selectedProduct, {
      quantity,
      batchId: selectedBatch?.id || undefined,
      batchStock: selectedBatch?.quantity,
      rate: displaySelling > 0 ? displaySelling : undefined,
    });

    setSelectedProduct(null);
    setBatchOptions([]);
    setSelectedBatchId("");
    setQuantity(1);
    setSearchTerm("");
    searchRef.current?.focus();
  };

  const handleSearchChange = (value: string) => {
    if (selectedProduct && value !== selectedProduct.name) {
      setSelectedProduct(null);
      setBatchOptions([]);
      setSelectedBatchId("");
    }
    setSearchTerm(value);
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("[data-search-item]");
      items[highlightIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  return (
    <div className="relative z-20 shrink-0">
      <div
        className={`rounded-xl border border-slate-200 bg-white shadow-sm transition-[padding] ${
          compact ? "p-2" : "p-3"
        }`}
      >
        <div
          className={`flex-wrap items-center justify-between gap-2 ${
            compact ? "hidden" : "mb-2 flex"
          }`}
        >
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Scan or search medicine
            </p>
            <p className="text-[11px] text-slate-500">
              Barcode, product name, SKU or composition
            </p>
          </div>
          <div className="hidden items-center gap-3 text-[11px] text-slate-500 md:flex">
            <span className="flex items-center gap-1.5">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                F2
              </kbd>
              Focus
            </span>
            <span className="flex items-center gap-1.5">
              <Keyboard className="h-3.5 w-3.5" />
              ↑↓ select · Enter confirm
            </span>
          </div>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scan barcode or search product"
              aria-label="Scan barcode or search product"
              className={`${compact ? "h-10" : "h-11"} w-full rounded-xl border border-teal-300 bg-white pl-10 pr-14 text-sm font-medium text-slate-800 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10`}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ScanBarcode className="h-4 w-4 text-slate-400" />
            </div>
          </div>

          <button
            type="button"
            onClick={handleAddSelected}
            disabled={!selectedProduct}
            className={`flex ${compact ? "h-10" : "h-11"} min-w-[112px] items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm shadow-teal-200 transition hover:bg-teal-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none`}
          >
            <Plus className="h-4 w-4" />
            Add item
          </button>
        </div>

        {selectedProduct ? (
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 lg:grid-cols-[minmax(0,1.7fr)_84px_96px_104px]">
            <label className="min-w-0">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Batch · FEFO selected
              </span>
              <select
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-700 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
              >
                {batchOptions.length === 0 && (
                  <option value="">Auto (FEFO)</option>
                )}
                {batchOptions.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Quantity
              </span>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Number(e.target.value) || 1))
                }
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-center text-sm font-semibold text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
              />
            </label>

            <div>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                MRP
              </span>
              <div className="flex h-9 items-center justify-end rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs font-semibold tabular-nums text-slate-700">
                ₹{displayMrp.toFixed(2)}
              </div>
            </div>

            <div>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Selling rate
              </span>
              <div className="flex h-9 items-center justify-end rounded-lg border border-teal-200 bg-teal-50 px-3 text-xs font-bold tabular-nums text-teal-700">
                ₹{displaySelling.toFixed(2)}
              </div>
            </div>
          </div>
        ) : !compact ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-0.5 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-teal-500" />
              Barcode scans quick-add the earliest-expiring in-stock batch
            </span>
            <span>Batch and price controls appear after product selection</span>
          </div>
        ) : null}
      </div>

      {!selectedProduct &&
        searchTerm.length >= 2 &&
        searchResults.length === 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-slate-200 bg-white p-7 text-center shadow-2xl">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100">
              <Package className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700">
              No products found
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Try a different name, SKU, or barcode
            </p>
          </div>
        )}

      {!selectedProduct && searchResults.length > 0 && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="sticky top-0 rounded-t-xl border-b border-slate-100 bg-slate-50/95 px-4 py-2 backdrop-blur">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {searchResults.length} result
              {searchResults.length !== 1 ? "s" : ""} — ↑↓ to navigate, Enter to
              select
            </span>
          </div>

          {searchResults.map((product, index) => {
            const stock = getStockStatus(product.totalStock);
            const schedule = getScheduleBadge(product.scheduleType as string);
            const isHighlighted = index === highlightIndex;
            const isOutOfStock = (product.totalStock ?? 0) <= 0;

            return (
              <button
                key={product.id}
                data-search-item
                onClick={() => {
                  setSelectedProduct(product);
                  setSearchTerm(product.name);
                }}
                className={`group flex w-full items-center gap-4 border-b border-slate-100 px-4 py-3 text-left transition last:border-0 ${
                  isHighlighted
                    ? "border-l-2 border-l-teal-500 bg-teal-50"
                    : "border-l-2 border-l-transparent hover:bg-slate-50"
                } ${isOutOfStock ? "opacity-60" : ""}`}
              >
                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`truncate text-sm font-semibold ${isHighlighted ? "text-teal-800" : "text-slate-900"}`}
                    >
                      {product.name}
                    </span>
                    {schedule && (
                      <span
                        className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${schedule.className}`}
                      >
                        {schedule.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-500">
                      {product.sku}
                    </span>
                    {product.manufacturer && (
                      <span className="truncate text-[11px] text-slate-500">
                        {product.manufacturer}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stock Badge */}
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[11px] font-semibold ${stock.color}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${stock.dot}`} />
                  {stock.label}
                </div>

                {/* Price */}
                <div className="text-right shrink-0 w-24">
                  <div className="font-bold text-teal-600 text-sm">
                    ₹{Number(product.sellingPrice).toFixed(2)}
                  </div>
                  {Number(product.mrp) !== Number(product.sellingPrice) && (
                    <div className="text-[10px] text-gray-400 line-through">
                      MRP ₹{Number(product.mrp).toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Add indicator */}
                <ChevronRight
                  className={`h-4 w-4 shrink-0 transition-transform ${isHighlighted ? "translate-x-0.5 text-teal-500" : "text-slate-300 group-hover:text-slate-400"}`}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
